// ─── src/chat/AuraBottomSheet.jsx ────────────────────────────────────────────
// Mobile-only Aura surface. Replaces AuraMiniBar on viewports < 768px.
//
// Responsibilities:
//   - Render the full message thread on mobile
//   - Show a horizontal recommendation carousel when recs are available
//   - Provide open/close interaction via backdrop + close button
//   - Expand to near-full height when chatUiState === 'fullchat'
//   - Respect safe-area-inset-bottom for notched devices
//
// Layout owned by FloatingUiManager (bottomSheetStyle, Z, safe area).
// All chat state owned by AuraCore.
//
// Intentionally excludes: gesture physics, spring animations, voice input,
// swipe velocity, advanced transitions, those are Phase 3+.

import { useState, useRef, useEffect, useCallback } from "react";
import { useAura }       from "../stores/auraCore";
import { useFloatingUi } from "../components/FloatingUiManager";
import { listingProfileUrl } from "../utils/listingUrl";

// ─── Design tokens ────────────────────────────────────────────────────────────
const GOLD       = "#d39a21";
const DARK_BG    = "#1A1816";
const THREAD_BG  = "#FFFDF9";
const BORDER     = "rgba(211,154,33,0.22)";

const KEYFRAMES = `
  @keyframes abs-slide-up {
    from { transform: translateY(100%); opacity: 0; }
    to   { transform: translateY(0);    opacity: 1; }
  }
  @keyframes abs-fade-bg {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
`;

// ─── Recommendation card ──────────────────────────────────────────────────────
// Tap focuses this business in Aura and triggers a concise intro reply (see
// focusRecommendation in auraCore). Because these live in a horizontal scroll
// carousel, a pointer-move threshold distinguishes a genuine tap from a swipe,
// so scrolling never accidentally activates a card.
function RecCard({ item }) {
  const focusRecommendation = useAura((s) => s.focusRecommendation);
  const [pressed, setPressed] = useState(false);
  const downRef = useRef(null);

  const onDown = (e) => {
    downRef.current = { x: e.clientX, y: e.clientY };
    setPressed(true);
  };
  const clearPress = () => setPressed(false);
  const onUp = (e) => {
    setPressed(false);
    const d = downRef.current;
    downRef.current = null;
    if (!d) return;
    // Swipe, not tap, let the carousel scroll, don't activate.
    if (Math.hypot(e.clientX - d.x, e.clientY - d.y) > 10) return;
    focusRecommendation(item.id);
  };

  const img = item.imgs?.[0];
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`Ask Aura about ${item.name}`}
      onPointerDown={onDown}
      onPointerUp={onUp}
      onPointerLeave={clearPress}
      onPointerCancel={clearPress}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); focusRecommendation(item.id); }
      }}
      style={{
        flexShrink:   0,
        width:        138,
        borderRadius: 10,
        overflow:     "hidden",
        background:   "#111",
        border:       "1px solid rgba(255,255,255,0.07)",
        cursor:       "pointer",
        transform:    pressed ? "scale(0.96)" : "scale(1)",
        opacity:      pressed ? 0.85 : 1,
        transition:   "transform 0.12s ease, opacity 0.12s ease",
        touchAction:  "pan-x",
        userSelect:   "none",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      {img ? (
        <div
          style={{
            height:     90,
            background: `url(${img}) center/cover no-repeat`,
          }}
        />
      ) : (
        <div
          style={{
            height:     90,
            background: "rgba(211,154,33,0.08)",
            display:    "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize:   20,
            color:      "rgba(211,154,33,0.4)",
          }}
        >✦</div>
      )}
      <div style={{ padding: "8px 9px 9px" }}>
        <div style={{
          fontSize:     11.5,
          fontWeight:   600,
          color:        "#f5f0e8",
          fontFamily:   "var(--font-body)",
          lineHeight:   1.3,
          marginBottom: 3,
          whiteSpace:   "nowrap",
          overflow:     "hidden",
          textOverflow: "ellipsis",
        }}>{item.name}</div>
        <div style={{
          fontSize:   10,
          color:      "rgba(245,240,232,0.45)",
          fontFamily: "var(--font-body)",
          whiteSpace: "nowrap",
          overflow:   "hidden",
          textOverflow: "ellipsis",
        }}>{item.city}{item.country ? `, ${item.country}` : ""}</div>
        {item.priceFrom && (
          <div style={{
            marginTop:  5,
            fontSize:   10,
            color:      GOLD,
            fontFamily: "var(--font-body)",
            fontWeight: 600,
          }}>{item.priceFrom}</div>
        )}
      </div>
    </div>
  );
}

// ─── Horizontal recommendations carousel ─────────────────────────────────────
function RecsCarousel({ items, loading }) {
  if (loading) {
    return (
      <div style={{
        padding:    "10px 16px",
        display:    "flex",
        gap:         10,
        overflowX:  "hidden",
      }}>
        {[0, 1, 2].map((i) => (
          <div key={i} style={{
            flexShrink: 0,
            width:      138,
            height:     145,
            borderRadius: 10,
            background: "rgba(255,255,255,0.05)",
            animation:  `abs-fade-bg 1.2s ease ${i * 0.15}s infinite alternate`,
          }} />
        ))}
      </div>
    );
  }

  if (!items?.length) return null;

  return (
    <div>
      <div style={{
        padding:      "8px 16px 4px",
        fontSize:     10,
        fontWeight:   700,
        letterSpacing: "1px",
        textTransform: "uppercase",
        color:        "rgba(211,154,33,0.65)",
        fontFamily:   "var(--font-body)",
      }}>Recommended</div>
      <div
        style={{
          display:           "flex",
          gap:                10,
          overflowX:         "auto",
          padding:           "6px 16px 12px",
          scrollbarWidth:    "none",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {items.map((item) => (
          <RecCard key={item.id} item={item} />
        ))}
      </div>
    </div>
  );
}

// ─── Typing indicator ────────────────────────────────────────────────────────
function TypingDots() {
  return (
    <div style={{ display: "flex", gap: 5, padding: "4px 2px" }}>
      {[0, 1, 2].map((i) => (
        <span key={i} style={{
          width:      6,
          height:     6,
          borderRadius: "50%",
          background: "rgba(211,154,33,0.5)",
          display:    "inline-block",
          animation:  `lwd-dot-pulse 1.4s ease ${i * 0.2}s infinite`,
        }} />
      ))}
    </div>
  );
}

// streamStage/streamingText are null whenever streaming isn't active — TypingDots
// renders unchanged then. See WorkspaceCenter.jsx for the same pattern on desktop.
function TypingIndicator({ streamStage, streamingText }) {
  if (streamingText) {
    return (
      <div style={{ padding: "4px 2px", fontSize: 13.5, lineHeight: 1.5, color: "#3a3530" }}>
        {streamingText}
        <span style={{ display: "inline-block", width: 5, height: 12, marginLeft: 2, background: "rgba(211,154,33,0.7)", verticalAlign: "text-bottom", animation: "lwd-cursor-blink 0.8s steps(1) infinite" }} />
      </div>
    );
  }
  if (streamStage) {
    return (
      <div style={{ display: "flex", gap: 7, alignItems: "center", padding: "4px 2px" }}>
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: "rgba(211,154,33,0.5)", display: "inline-block", animation: "lwd-dot-pulse 1.4s ease infinite" }} />
        <span style={{ fontSize: 12.5, color: "rgba(58,53,48,0.65)" }}>{streamStage.label}</span>
      </div>
    );
  }
  return <TypingDots />;
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function AuraBottomSheet() {
  const chatUiState          = useAura((s) => s.chatUiState);
  const messages             = useAura((s) => s.messages);
  const isTyping             = useAura((s) => s.isTyping);
  const streamStage          = useAura((s) => s.streamStage);
  const streamingText        = useAura((s) => s.streamingText);
  const sendMessage          = useAura((s) => s.sendMessage);
  const closeChat            = useAura((s) => s.closeChat);
  const openWorkspace        = useAura((s) => s.openWorkspace);
  const recommendations      = useAura((s) => s.recommendations);
  const recommendationsLoading = useAura((s) => s.recommendationsLoading);
  const auraShortlistVenue   = useAura((s) => s.auraShortlistVenue);
  const auraShortlistedIds   = useAura((s) => s.auraShortlistedIds);

  const { bottomSheetStyle, Z } = useFloatingUi();

  const [input,   setInput]   = useState("");
  const threadRef             = useRef(null);
  const inputRef              = useRef(null);

  const expanded = chatUiState === "fullchat";
  const hasRecs  = (recommendations?.items?.length ?? 0) > 0;

  // Auto-scroll thread on new messages and when the recommendation carousel renders
  useEffect(() => {
    const scroll = () => {
      if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
    };
    scroll();
    // Second pass after carousel images have had a chance to lay out
    const t = setTimeout(scroll, 120);
    return () => clearTimeout(t);
  }, [messages, isTyping, recommendations]);

  // Focus input on open
  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 350);
    return () => clearTimeout(t);
  }, []);

  const handleSend = useCallback(() => {
    const text = input.trim();
    if (!text) return;
    sendMessage(text);
    setInput("");
  }, [input, sendMessage]);

  const handleKey = useCallback((e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  // Sheet height driven by state, simple CSS transition, no spring physics
  const sheetMaxHeight = expanded ? "90vh" : "65vh";

  return (
    <>
      <style>{KEYFRAMES}</style>

      {/* Backdrop */}
      <div
        onClick={closeChat}
        style={{
          position:          "fixed",
          inset:              0,
          zIndex:             Z.auraMini - 1,
          background:        "rgba(10,9,7,0.45)",
          backdropFilter:    "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
          animation:         "abs-fade-bg 0.25s ease both",
        }}
      />

      {/* Sheet */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Aura chat"
        style={{
          ...bottomSheetStyle,
          zIndex:         Z.auraMini,
          borderRadius:   "20px 20px 0 0",
          background:     DARK_BG,
          border:        `1px solid ${BORDER}`,
          borderBottom:   "none",
          boxShadow:      "0 -16px 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(211,154,33,0.06)",
          display:        "flex",
          flexDirection:  "column",
          maxHeight:       sheetMaxHeight,
          transition:     "max-height 0.3s ease",
          animation:      "abs-slide-up 0.32s cubic-bezier(0.25,0.46,0.45,0.94) both",
          // Override bottomSheetStyle paddingBottom, we handle safe area in input row
          paddingBottom:  0,
        }}
      >
        {/* Drag handle pill */}
        <div style={{
          display:        "flex",
          justifyContent: "center",
          padding:        "10px 0 4px",
          flexShrink:      0,
        }}>
          <div style={{
            width:        36,
            height:        4,
            borderRadius:  2,
            background:   "rgba(255,255,255,0.15)",
          }} />
        </div>

        {/* Header */}
        <div style={{
          display:        "flex",
          alignItems:     "center",
          justifyContent: "space-between",
          padding:        "10px 18px 11px",
          borderBottom:  `1px solid rgba(211,154,33,0.10)`,
          flexShrink:      0,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width:          30,
              height:         30,
              borderRadius:  "50%",
              background:    "rgba(211,154,33,0.12)",
              border:        "1px solid rgba(211,154,33,0.30)",
              display:       "flex",
              alignItems:    "center",
              justifyContent: "center",
              fontSize:       13,
              color:          GOLD,
              flexShrink:     0,
            }}>✦</div>
            <div>
              <div style={{
                fontFamily:   "var(--font-body)",
                fontWeight:    600,
                fontSize:      13,
                color:        "#f5f0e8",
                letterSpacing: "0.3px",
                lineHeight:    1,
                marginBottom:  3,
              }}>Chat with Aura</div>
              <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <span style={{
                  width: 6, height: 6, borderRadius: "50%",
                  background: "#22c55e", display: "inline-block",
                }} />
                <span style={{
                  fontFamily:    "var(--font-body)",
                  fontSize:       10,
                  color:         "rgba(245,240,232,0.4)",
                  letterSpacing: "0.5px",
                }}>Online</span>
              </div>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {/* Expand to fullchat when in 'open' state */}
            {!expanded && (
              <button
                onClick={openWorkspace}
                aria-label="Expand chat"
                style={{
                  background:    "rgba(211,154,33,0.08)",
                  border:       "1px solid rgba(211,154,33,0.22)",
                  borderRadius:  6,
                  color:         GOLD,
                  padding:      "5px 10px",
                  fontFamily:   "var(--font-body)",
                  fontSize:      11,
                  fontWeight:    600,
                  cursor:        "pointer",
                  letterSpacing: "0.3px",
                  whiteSpace:   "nowrap",
                }}
              >Expand ↑</button>
            )}
            <button
              onClick={closeChat}
              aria-label="Close chat"
              style={{
                width:          30,
                height:         30,
                borderRadius:  "50%",
                background:    "rgba(255,255,255,0.06)",
                border:        "none",
                color:         "rgba(245,240,232,0.45)",
                cursor:        "pointer",
                fontSize:       17,
                display:       "flex",
                alignItems:    "center",
                justifyContent: "center",
              }}
            >×</button>
          </div>
        </div>

        {/* Message thread */}
        <div
          ref={threadRef}
          style={{
            flex:       "1 1 auto",
            overflowY:  "auto",
            padding:    "12px 16px",
            display:    "flex",
            flexDirection: "column",
            gap:         8,
            background:  THREAD_BG,
            WebkitOverflowScrolling: "touch",
          }}
        >
          {messages.map((m) => (
            <div
              key={m.id}
              style={{
                display:       "flex",
                flexDirection: "column",
                alignItems:    m.from === "user" ? "flex-end" : "flex-start",
                gap:            3,
              }}
            >
              <div style={{
                maxWidth:     "84%",
                padding:      "8px 12px",
                borderRadius: m.from === "user"
                  ? "10px 10px 2px 10px"
                  : "10px 10px 10px 2px",
                background:   m.from === "user" ? GOLD : "#F0ECE4",
                fontFamily:   "var(--font-body)",
                fontSize:      13,
                lineHeight:    1.5,
                whiteSpace:   "pre-line",
                color:         m.from === "user" ? "#0f0d0a" : "#3a3530",
              }}>{m.text}</div>

              {/* Recommendation intro actions, View profile + Save, sitting
                  directly beneath Aura's introduction so the full listing is one
                  tap away without leaving the conversation. */}
              {m._recIntroFor && (() => {
                const recItem = recommendations?.items?.find((r) => r.id === m._recIntroFor);
                if (!recItem) return null;
                const saved = auraShortlistedIds.includes(recItem.id);
                const profileUrl = listingProfileUrl(recItem);
                return (
                  <div style={{ display: "flex", gap: 6, marginTop: 5 }}>
                    {profileUrl && <a
                      href={profileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        fontFamily:    "var(--font-body)",
                        fontSize:       11,
                        fontWeight:     700,
                        letterSpacing:  "0.4px",
                        textTransform:  "uppercase",
                        color:          "#0D0B09",
                        background:      GOLD,
                        borderRadius:    7,
                        padding:        "6px 12px",
                        textDecoration:  "none",
                      }}
                    >View profile</a>}
                    <button
                      onClick={() => auraShortlistVenue(recItem)}
                      disabled={saved}
                      style={{
                        fontFamily:    "var(--font-body)",
                        fontSize:       11,
                        fontWeight:     600,
                        letterSpacing:  "0.4px",
                        textTransform:  "uppercase",
                        color:          GOLD,
                        background:     "rgba(211,154,33,0.10)",
                        border:         "1px solid rgba(211,154,33,0.35)",
                        borderRadius:    7,
                        padding:        "6px 12px",
                        cursor:          saved ? "default" : "pointer",
                        opacity:         saved ? 0.6 : 1,
                      }}
                    >{saved ? "Saved ✓" : "Save"}</button>
                  </div>
                );
              })()}
            </div>
          ))}
          {isTyping && <TypingIndicator streamStage={streamStage} streamingText={streamingText} />}
        </div>

        {/* Recommendation carousel */}
        {(hasRecs || recommendationsLoading) && (
          <div style={{
            background:   DARK_BG,
            borderTop:   `1px solid rgba(211,154,33,0.08)`,
            flexShrink:   0,
          }}>
            <RecsCarousel
              items={recommendations?.items}
              loading={recommendationsLoading}
            />
          </div>
        )}

        {/* Input row */}
        <div style={{
          padding:     "10px 14px",
          paddingBottom: "calc(10px + env(safe-area-inset-bottom, 0px))",
          borderTop:  `1px solid rgba(0,0,0,0.06)`,
          background:  THREAD_BG,
          flexShrink:  0,
        }}>
          <div style={{
            display:    "flex",
            gap:         6,
            alignItems: "center",
            background: "#F5F2ED",
            border:    `1px solid rgba(211,154,33,0.15)`,
            borderRadius: 12,
            padding:    "6px 8px",
          }}>
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Guest count, style, budget…"
              style={{
                flex:       1,
                background: "none",
                border:     "none",
                padding:    "6px 4px",
                fontFamily: "var(--font-body)",
                fontSize:    14,
                color:      "#1a1816",
                outline:    "none",
                caretColor:  GOLD,
              }}
            />
            <button
              onClick={handleSend}
              disabled={!input.trim()}
              aria-label="Send message"
              style={{
                width:        34,
                height:       34,
                flexShrink:   0,
                borderRadius:  8,
                background:   input.trim() ? GOLD : "rgba(211,154,33,0.12)",
                border:       "none",
                color:        input.trim() ? "#0f0d0a" : "rgba(211,154,33,0.35)",
                cursor:       input.trim() ? "pointer" : "default",
                fontSize:      16,
                display:      "flex",
                alignItems:   "center",
                justifyContent: "center",
                transition:   "background 0.2s, color 0.2s",
              }}
            >↑</button>
          </div>
        </div>
      </div>
    </>
  );
}
